import { useState, useEffect, useCallback } from "react";
import {
  Drawer,
  List,
  ListItemButton,
  IconButton,
  Box,
  Avatar,
  AppBar,
  Toolbar,
  Typography,
} from "@mui/material";

import { styled } from "@mui/material/styles";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { EnvironmentVariables } from "@/modules/common/types";
import { PathConstants } from "@/routes/constants/index";
import UploadLeadsModal from "@/modules/upload-leads/components/upload-leads-modal";
import { useOrganization } from "@/modules/common/context/organizations/get-user-organizations.provider";
import NotificationBell from "@/modules/common/components/layout/side-menu/components/notification-bell";
import Meetings from "@/modules/meetings/components/meetings/meetings-model";
import MobileModal from "@/modules/common/components/ui-kit/modals/mobile-modal";
import Tasks from "@/modules/meetings/components/tasks/tasks-model";
import { MenuListIcon } from "@/modules/common/components/ui-kit/icons/menu-list-icon";
import { ChevronLeftIcon } from "@/modules/common/components/ui-kit/icons/chevron-left-icon";
import { nawyTheme } from "@/styles/nawy-theme/nawy-theme";
import { useUserAvailability } from "@/modules/common/hooks/user-availability.hook";
import { SettingsList } from "@/modules/common/components/layout/side-menu/components/mobile/settings-list";
import { MoreSection } from "@/modules/common/components/layout/side-menu/components/mobile/more-list";
import { HelpListItem } from "@/modules/common/components/layout/side-menu/components/mobile/help-list";
import { MainSidebarList } from "@/modules/common/components/layout/side-menu/components/mobile/main-list";
import { ExtraList } from "@/modules/common/components/layout/side-menu/components/mobile/extra-list";
import { UserMenuDropdown } from "./components/mobile/user-menu";

const StyledDrawer = styled(Drawer)(() => ({
  width: 280,
  flexShrink: 0,
  "& .MuiDrawer-paper": {
    width: 280,
    backgroundColor: nawyTheme.nawy.colors.semantic.layer.accentDark,
    color: nawyTheme.nawy.colors.primitives.white[100],
  },
}));

const StyledListItemButton = styled(ListItemButton)(() => ({
  color: nawyTheme.nawy.colors.primitives.white[100],

  "&.Mui-selected": {
    backgroundColor: nawyTheme.nawy.colors.semantic.layer.accent,
  },
}));

const SubMenuListItemButton = styled(ListItemButton)(() => ({
  color: nawyTheme.nawy.colors.primitives.white[100],
  paddingLeft: nawyTheme.nawy.spacing.xxs,
  "&:hover": {
    backgroundColor: nawyTheme.nawy.colors.semantic.layer.accentDark,
    color: nawyTheme.nawy.colors.primitives.white[100],
  },
  "&.Mui-selected": {
    backgroundColor: nawyTheme.nawy.colors.semantic.layer.accent,
  },
}));

const StyledAppBar = styled(AppBar)({
  backgroundColor: nawyTheme.nawy.colors.semantic.layer.accentDark,
  position: "fixed",
  height: "64px",
  zIndex: 1200,
});

const erealtyURL = `${EnvironmentVariables.EREALTY_URL}`;
const isFranchise = `${EnvironmentVariables.IS_FRANCHISE}`;

export const MobileNavigationSidebar: React.FC = () => {
  const {
    availabilityData,
    toggleUserAvailability,
    logout,
    isAvailable,
    isAutodialerActive,
  } = useUserAvailability();
  const { selectedOrganization, userTiers } = useOrganization();
  const [logoURL, setLogoURL] = useState("");
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [toggleUploadLeadsModal, setToggleUploadLeadsModal] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalContent, setModalContent] = useState<
    "calendar" | "clipboard" | null
  >(null);

  const location = useLocation();
  const navigate = useNavigate();

  const handleCalendarClick = () => {
    setModalContent("calendar");
    setModalOpen(true);
    setSidebarOpen(false);
  };

  const handleClipboardClick = () => {
    setModalContent("clipboard");
    setModalOpen(true);
    setSidebarOpen(false);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setModalContent(null);
  };

  const [anchorElSettings, setAnchorElSettings] = useState<null | HTMLElement>(
    null
  );
  const [anchorElUserMenu, setAnchorElUserMenu] = useState<null | HTMLElement>(
    null
  );

  const handleOpenSettings = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorElSettings(event.currentTarget);
  };

  const handleOpenUserMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorElUserMenu(event.currentTarget);
  };

  const handleCloseSettings = () => setAnchorElSettings(null);
  const handleCloseUserMenu = () => setAnchorElUserMenu(null);

  const showUploadLeadsModal = () => {
    setToggleUploadLeadsModal(!toggleUploadLeadsModal);
    setSidebarOpen(false);
  };

  const getNavLinkStyles = useCallback(
    (navPath: string) => {
      if (location.pathname === navPath) {
        return true;
      }
      return false;
    },
    [location.pathname]
  );

  const handleItemClick = (path?: string) => {
    setSidebarOpen(false);
    if (path) {
      navigate(path);
    }
  };

  useEffect(() => {
    selectedOrganization?.logo?.link &&
      setLogoURL(selectedOrganization?.logo?.link);
  }, [selectedOrganization]);

  return (
    <>
      <StyledAppBar>
        <Toolbar sx={{ justifyContent: "space-between" }}>
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <IconButton
              color="inherit"
              aria-label="open drawer"
              onClick={() => setSidebarOpen(true)}
              edge="start"
              sx={{ mr: 2 }}
            >
              <MenuListIcon />
            </IconButton>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                maxWidth: "50px",
                maxHeight: "20px",
                marginRight: "30px",
                marginLeft: "20px",
                marginTop: "10px",
              }}
            >
              <Link to={PathConstants.HOME} style={{ textDecoration: "none" }}>
                {logoURL ? (
                  <img src={logoURL} alt="logo" width="130px" />
                ) : null}
              </Link>
            </Box>
          </Box>

          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <SettingsList
              anchorEl={anchorElSettings}
              handleClose={handleCloseSettings}
              handleItemClick={handleItemClick}
              handleOpen={handleOpenSettings}
            />
            <NotificationBell
              variant="icon"
              href={PathConstants.NOTIFICATIONS_CENTER}
            />

            <Avatar
              onClick={handleOpenUserMenu}
              sx={{
                cursor: "pointer",
                width: "34px",
                height: "34px",
                ...nawyTheme.nawy.typography.english.variants.body.md.regular,
                color: nawyTheme.nawy.colors.primitives.white[100],
                backgroundColor: availabilityData?.data?.user?.color,
              }}
            >
              {availabilityData?.data?.user?.name?.charAt(0)}
            </Avatar>
            <UserMenuDropdown
              anchorEl={anchorElUserMenu}
              onClose={handleCloseUserMenu}
              isFranchise={isFranchise}
              isAvailable={isAvailable}
              isAutodialerActive={isAutodialerActive}
              toggleUserAvailability={toggleUserAvailability}
              logout={logout}
              userTiers={userTiers}
            />
          </Box>
        </Toolbar>
      </StyledAppBar>

      <StyledDrawer
        variant="temporary"
        anchor="left"
        open={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
      >
        <Box
          onClick={() => setSidebarOpen(false)}
          sx={{
            margin: nawyTheme.nawy.spacing.xs,
            alignSelf: "flex-start",
            cursor: "pointer",
            padding: nawyTheme.nawy.spacing.xxs,
            borderRadius: nawyTheme.nawy.radius.xl,
            border: `1px solid rgba(255, 255, 255, 0.3)`,
          }}
        >
          <ChevronLeftIcon />
        </Box>
        <Box
          sx={{
            overflow: "auto",
            height: "100%",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <List sx={{ flexGrow: 1 }}>
            <MainSidebarList
              getNavLinkStyles={getNavLinkStyles}
              handleItemClick={handleItemClick}
              showUploadLeadsModal={showUploadLeadsModal}
              StyledListItemButton={StyledListItemButton}
              SubMenuListItemButton={SubMenuListItemButton}
            />

            {/* More Section */}
            <MoreSection
              handleItemClick={handleItemClick}
              erealtyURL={erealtyURL}
              showUploadLeadsModal={showUploadLeadsModal}
              StyledListItemButton={StyledListItemButton}
              SubMenuListItemButton={SubMenuListItemButton}
              getNavLinkStyles={getNavLinkStyles}
            />
            {/* Help Section */}
            <HelpListItem
              handleItemClick={handleItemClick}
              showUploadLeadsModal={showUploadLeadsModal}
              StyledListItemButton={StyledListItemButton}
              SubMenuListItemButton={SubMenuListItemButton}
              getNavLinkStyles={getNavLinkStyles}
            />
          </List>

          <ExtraList
            handleCalendarClick={handleCalendarClick}
            handleClipboardClick={handleClipboardClick}
            getNavLinkStyles={getNavLinkStyles}
            handleItemClick={handleItemClick}
            StyledListItemButton={StyledListItemButton}
            SubMenuListItemButton={SubMenuListItemButton}
          />
        </Box>
      </StyledDrawer>

      <UploadLeadsModal
        toggleUploadLeadsModal={toggleUploadLeadsModal}
        setToggleUploadLeadsModal={setToggleUploadLeadsModal}
      />

      <MobileModal
        open={modalOpen}
        onClose={handleCloseModal}
        title={modalContent === "calendar" ? "Meetings" : "Tasks"}
        headerAction={
          modalContent === "clipboard" ? (
            <Link
              to={PathConstants.TASK_DASHBOARD}
              style={{ textDecoration: "none" }}
              onClick={handleCloseModal}
            >
              <Typography
                sx={{
                  color: nawyTheme.nawy.colors.semantic.text.accent,
                  ...nawyTheme.nawy.typography.english.variants.body.md
                    .semibold,
                  fontFamily: nawyTheme.nawy.typography.english.fontFamily,
                  transition: "all 0.2s ease",
                  "&:hover": {
                    cursor: "pointer",
                  },
                }}
              >
                All Tasks
              </Typography>
            </Link>
          ) : undefined
        }
      >
        {modalContent === "calendar" && (
          <Box sx={{ maxHeight: "100%", overflow: "auto" }}>
            <Meetings />
          </Box>
        )}
        {modalContent === "clipboard" && (
          <Box sx={{ height: "100%", overflow: "auto" }}>
            <Tasks />
          </Box>
        )}
      </MobileModal>
    </>
  );
};

export default MobileNavigationSidebar;
